/* istanbul ignore file */
/**
 * Created by rathompson on 9/13/21
 * File: IdentitiesTable
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useState, VFC } from 'react'
import { dispatch as globalDispatch, actions, Tsg } from '@sparky/framework'
// @ts-ignore
import { useReactOidc } from '@axa-fr/react-oidc-context'
import {
  useModal,
  Button,
  TruncateText,
} from '@panwds/react-ui'
import {
  ConnectedTable,
  TableProvider,
  ConnectedTableLayout,
  useSelectedRowData,
  ConnectedPaginator,
} from '@panwds/react-table'
import { LoadTreeData } from '../../../../../common/actions/GlobalActions'
import {
  LABEL_HEADER_TYPE,
  LABEL_HEADER_DISPLAY_NAME,
  LABEL_HEADER_IDENTITY,
  LABEL_HEADER_APPLICATIONS,
  LABEL_HEADER_ROLE,
  LABEL_INHERITED,
  BUTTON_REMOVE_ROLES,
  BUTTON_ASSIGN_ROLES,
  PLACEHOLDER_SEARCH,
  LABEL_HEADER_SUBTITLE,
  LABEL_IDENTITIES_SUBTENANT,
  MESSAGE_MULTI_IDENTITIES_REMOVED,
  MESSAGE_SINGLE_IDENTITY_REMOVED,
  LABEL_HEADER_SCOPE,
  BUTTON_ADD_IDENTITY,
  LABEL_ASSIGN_ROLES,
  LABEL_HEADER_ACTIONS,
  TOOLTIP_SELECTED_LAST_SUPERUSER,
  TOOLTIP_SELECTED_INHERITED_POLICY, TOOLTIP_READ_ONLY
} from '../../../../constants/LabelConstants'
import TableHeader from '../../../../../common/components/table/TableHeader'
import ViewIdentityModal from '../../modal/ViewIdentityModal'
import TypeRenderer from './../cellRenderer/TypeRenderer'
import RoleRenderer from './../cellRenderer/RoleRenderer'
import IdentityAddressRenderer from './../cellRenderer/IdentityAddressRenderer'
import DisplayNameRenderer from './../cellRenderer/DisplayNameRenderer'
import ApplicationsRenderer from './../cellRenderer/ApplicationsRenderer'
import InheritedCellRenderer from './../cellRenderer/InheritedCellRenderer'
import AddIdentityModal from '../../modal/AddIdentityModal'
import IdentityModal from '../../modal/IdentityModal/IdentityModal'
import FiltersBar from './../FiltersBar'
import AssignRolesModal from '../../modal/AssignRolesModal/AssignRolesModal'
import AssignRolesModalOld from '../../modal/AssignRolesModal/AssignRolesModalOld'
import RemoveIdentityModal from '../../modal/RemoveIdentityModal'

import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import { IDENTITIES_PERMISSION_SET, IDENTITY_TYPES } from '../../../../constants/IAMConstants'
import IAMService from '../../../../services/IAMService'
import { getVarValue } from '../../../../../common/utils/Sparky'
import { selectTreeNode } from '../../../../../common/reducers/actions/TreeReducer.actions'

import './../IdentitiesTable.scss'
import {
  FF_DEFAULT_TSG,
  FF_DISPLAY_SCOPE_COLUMN,
  FF_ENABLE_SERVICE_ACCOUNT,
  FF_IAM_PHASE_2,
  FF_ROLES_ENABLED,
} from '../../../../../common/utils/FeatureFlags'
import { ROOT_NODE_ID } from '../../../../../tenancy/constants/TenancyConstants'
import { useMessage } from '../../../../../common/providers/MessageProvider'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import GlobalContext from '../../../../../common/context/GlobalContext'
import ConnectedTableFilter from '../../../../../common/components/table/tableFilter/ConnectedTableFilter'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import { useLoadingContext } from '../../../../../common/context/LoadingContext'
import ScopeRenderer from './../cellRenderer/ScopeRenderer'
import IAMAppName from '../../../../utils/IAMAppName'
import AppContext from '../../../../../common/context/AppContext'
import ActionsRenderer from './../cellRenderer/ActionsRenderer'

import {
  isChildOf,
  getRoles,
  getApps,
  getInherited,
  getMembers,
  defaultSort,
  sortRoles,
  sortInherited,
  sortApps
} from './utils'
import RemovePolicyModal from '../../modal/RemovePolicyModal'
import AuthContext from '../../../../../common/context/AuthContext'
import useReadOnly from '../../../../../common/hooks/useReadOnly'
import TooltipButton from '../../../../../common/components/tooltipButton/TooltipButton'

const enableService = getVarValue(FF_ENABLE_SERVICE_ACCOUNT, false)
const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)
const displayScopeColumn = getVarValue(FF_DISPLAY_SCOPE_COLUMN, false)
const iamPhase2 = getVarValue(FF_IAM_PHASE_2, false)
const enableDefaultTSG = getVarValue(FF_DEFAULT_TSG)

/***
 *
 * @returns {JSX.Element}
 * @constructor
 * @desc Displays all identities found for the currently selected tenant, has search filter and pagination controls
 */
const IdentitiesTable: VFC<{
  getData: Function,
  canListPolicies: boolean
}> = ({ getData: _getData, canListPolicies }) => {
  const service = IAMService
  const {
    treeReducer,
    iamReducer,
    dispatch
  } = useContext(GlobalContext)
  const { user } = useContext(AuthContext)
  const { globalTsgId, sgKeyBy, selectedTreeNode, flattenTree } = treeReducer
  const {
    accessPolicies,
    serviceAccounts,
    roleMap,
    customRoleMap
  } = iamReducer
  const { loading } = useLoadingContext()

  /**
   *     ========= Local State ===========
   */
  const { oidcUser } = useReactOidc()
  const [sortBy, setSortBy] = useState<{
    id?: any
  } | null>(null)
  const [data, setData] = useState([])
  const [member, setMember] = useState<{
    identity_id: string,
    ids: string[]
  } | null>(null)
  const [resolvedData, setResolvedData] = useState([])
  const [filters, setFilters] = useState<{
    type?: any,
    app?: any,
    role?: any,
    inheritedBy?: any
  }>({})

  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)
  const { createBanner } = useMessage()

  const viewModal = useModal(ViewIdentityModal)
  const addModal = useModal((enableService ? IdentityModal : AddIdentityModal) as any)
  const removeModal = useModal(RemoveIdentityModal)
  const assignRoles = useModal(iamPhase2 ? AssignRolesModal : AssignRolesModalOld)
  const removePolicyModal = useModal(RemovePolicyModal)

  const { getAppName } = useContext(AppContext)

  /**
   *     ========= Callback =============
   */
  const getNextTSG = useCallback(() => {
    const roots = flattenTree?.filter((tenant: any) => tenant.isRoot)
    const tenants = roots?.filter((tenant: any) => tenant?.id !== '_superRoot' && tenant?.id !== selectedTreeNode?.id)
    if (selectedTreeNode?.parent_id !== '_superRoot') {
      return selectedTreeNode
    }
    if (selectedTreeNode?.id !== globalTsgId) {
      const globalTsg = flattenTree.find((tsg: Tsg) => tsg?.id === globalTsgId)
      if (isChildOf(selectedTreeNode, globalTsg)) {
        return tenants[0] || globalTsg
      }
      return globalTsg
    }
    return tenants[0]
  }, [flattenTree, globalTsgId, selectedTreeNode])

  useEffect(() => {
    if (rolesEnabled) {
      setData(getMembers(accessPolicies as any, serviceAccounts as any, sgKeyBy as any) as any)
    }
  }, [accessPolicies, serviceAccounts, sgKeyBy])

  const apps = useMemo(() => getApps(accessPolicies as any, (value: any) => IAMAppName(value, getAppName)), [accessPolicies, getAppName])

  const roles = useMemo(() => getRoles(accessPolicies as any, roleMap, customRoleMap), [accessPolicies, roleMap, customRoleMap])

  const inherited = useMemo(() => getInherited(accessPolicies, sgKeyBy, selectedTreeNode), [accessPolicies, sgKeyBy, selectedTreeNode])

  const getData = useCallback(async (member) => {
    if (selectedTreeNode?.id && selectedTreeNode?.id !== '_superRoot') {
      try {
        setFilters({})
        if (rolesEnabled) {
          await _getData(selectedTreeNode?.id, dispatch)
        }
        else {
          const { data } = await service?.members.get(selectedTreeNode?.id) || {}
          setData(data)
        }
      }
      catch (e) {
        // TODO: Handle server errors
        // setError(e.message)
      }
      if (OBJECT.hasObject(member)) {
        setMember(member)
      }
      else {
        setMember(null)
      }
    }
  }, [_getData, dispatch, selectedTreeNode?.id, service?.members])

  const addIdentity = useCallback((tsg_id, { identity_type, identity_email }) => {
    return service?.members.post(tsg_id, { identity_type, identity_email })
  }, [service])

  const removeIdentity = useCallback(async (tsg_id, identities) => {
    const { email, subName } = user
    let response
    let isSelfRemove
    const sorted_identities = identities.sort((identity_a: any, identity_b: any) => {
      if (identity_a?.ids?.includes(subName) || identity_a?.identity_email === email) {
        return 1
      }
      if (identity_b?.ids?.includes(subName) || identity_b?.identity_email === email) {
        return -1
      }
      return 0
    })
    for (const identity of sorted_identities) {
      if (!isSelfRemove) {
        if (rolesEnabled) {
          isSelfRemove = identity?.ids?.includes(oidcUser?.profile?.sub) || identity?.identity_email === oidcUser?.profile?.email
        }
        else {
          isSelfRemove = identity?.identity_id === oidcUser?.profile?.sub
        }
      }
      if (rolesEnabled) {
        for (const policy of identity.policies) {
          const { tsg_id } = policy.resource
          if (tsg_id !== selectedTreeNode?.id) {
            continue
          }
          if (policy.source) {
            continue
          }
          response = await service?.accessPolicies.delete(tsg_id, policy?.id)
        }
      }
      if (identity?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT && identity?.tsg_id === tsg_id) {
        response = await service?.serviceAccounts.delete(tsg_id, identity?.identity_id || identity?.id)
      }
      else if (!rolesEnabled) {
        response = await service?.members.delete(tsg_id, identity?.id)
      }
    }
    if (isSelfRemove) {
      const tsg = getNextTSG()
      if (!tsg || !tsg?.id) {
        await actions?.logout()
      }
      if (tsg?.id !== selectedTreeNode?.id) {
        await (globalDispatch as any).switchTsg(tsg?.id, { forceReload: true })
        dispatch(selectTreeNode(tsg))
        LoadTreeData(dispatch)
        if (enableDefaultTSG && Object.keys(sgKeyBy).length === 2) {
          // need to reload auth state on self remove for single tenant view
          window.location.replace('')
        }
      }
    }
    const message = (identities.length > 1 ?
      StringTemplate(MESSAGE_MULTI_IDENTITIES_REMOVED, [identities?.length]) :
      StringTemplate(MESSAGE_SINGLE_IDENTITY_REMOVED, [identities[0]?.display_name || identities[0]?.identity_email])) as string
    createBanner({ message, bannerProps: { appearance: 'success', type: 'page' } })
    return response
  }, [createBanner, dispatch, getNextTSG, oidcUser?.profile?.email, oidcUser?.profile?.sub, selectedTreeNode?.id, service?.accessPolicies, service?.members, service?.serviceAccounts, sgKeyBy, user])

  const onRowClick = useCallback(async (row, tenant) => {
    if (enableService) {
      // TODO: Eventually Remove Service Account Feature Flag
      addModal.openModal({ tenant, getData, member: row.original, isEdit: true, addIdentity, removeIdentity, length: resolvedData?.length })
    }
    else {
      viewModal.openModal({ tenant, getData, removeIdentity, ...row.original })
    }
  }, [addIdentity, addModal, getData, removeIdentity, viewModal, resolvedData])

  const scrollToRow = useCallback((row) => {
    if (!rolesEnabled) {
      const rowElement = document.querySelector(`[aria-rowindex="${row?.index}"]`)
      rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    }
  }, [])

  const onSortChange = useCallback((sortBy) => {
    if (sortBy[0]) {
      setSortBy(sortBy[0])
    }
    else {
      setSortBy(null)
    }
  }, [])

  const shouldHighlightRow = useCallback(({ row }) => {
    if (!rolesEnabled) {
      const shouldHighlight = row?.original?.identity_id === member?.identity_id
      if (shouldHighlight) {
        scrollToRow(row)
      }
      return shouldHighlight
    }
    else {
      const shouldHighlight = row?.original?.ids?.some((id: string) => (member ? member?.ids?.indexOf(id) >= 0 : false))
      if (shouldHighlight) {
        scrollToRow(row)
      }
      return shouldHighlight
    }
  }, [member, scrollToRow])

  useEffect(() => {
    setMember(null)
    setResolvedData(data)
  }, [data])

  const onRemoveActionClick = useCallback((user, policy) => {
    removePolicyModal.openModal({
      policy,
      user,
      getData,
      removeIdentity
    } as any)
  }, [getData, removeIdentity, removePolicyModal])

  const onEditActionClick = useCallback((member) => {
    addModal.openModal({
      tenant: selectedTreeNode,
      getData,
      member,
      isEdit: true,
      addIdentity,
      removeIdentity,
      length: resolvedData?.length,
      sidebarOption: {
        name: LABEL_ASSIGN_ROLES
      }
    })
  }, [addIdentity, addModal, getData, removeIdentity, resolvedData?.length, selectedTreeNode])

  const onRemoveServiceAccount = useCallback((serviceAccount) => {
    removeModal.openModal({
      tenant: selectedTreeNode,
      getData,
      removeIdentity,
      identities: [serviceAccount],
    } as any)
  }, [getData, removeIdentity, removeModal, selectedTreeNode])

  const columns = useMemo(() => {
    const columns = [
      {
        accessor: (row: any) => row?.identity_type,
        Header: LABEL_HEADER_TYPE,
        Cell: TypeRenderer,
        id: 'identity_type',
        // sticky: true,
        // stickyPosition: 'left',
        disableSortBy: true,
        disableResizing: true,
        width: 100
      },
      {
        accessor: (row: any) => row.display_name?.trim() || row?.identity_email,
        Header: LABEL_HEADER_DISPLAY_NAME,
        Cell({ row, value }: any) {
          return (
            <DisplayNameRenderer
              {...{ row, value }}
              onClick={onRowClick.bind(this, row, selectedTreeNode)}
            />
          )
        },
        width: 232,
        id: 'display_name',
      },
      {
        accessor: (row: any) => row?.identity_email,
        Header: LABEL_HEADER_IDENTITY,
        Cell: IdentityAddressRenderer,
        width: 250,
        id: 'identity_email',
      },
      {
        accessor: (row: any) => row?.apps,
        Header: LABEL_HEADER_APPLICATIONS,
        Cell: ApplicationsRenderer,
        width: 232,
        id: 'applications',
      },
      {
        accessor: (row: any) => row?.roles,
        Header: LABEL_HEADER_ROLE,
        Cell: RoleRenderer,
        width: 232,
        id: 'role',
      },
      {
        accessor: (row: any) => row?.scopes,
        Header: LABEL_HEADER_SCOPE,
        Cell: ScopeRenderer,
        width: 190,
        id: 'scope'
      },
      {
        accessor: (row: any) => row?.inherited_from,
        Header: LABEL_INHERITED,
        Cell: (props: any) => <InheritedCellRenderer {...{ sgKeyBy, selectedTreeNode, ...props }}/>,
        width: 232,
        id: 'inherited_from',
      },
      {
        Header: LABEL_HEADER_ACTIONS,
        accessor: (row: any) => row?.identity_type,
        Cell: (props: any) => <ActionsRenderer {...{ ...props,
          onDeleteClick: onRemoveActionClick,
          onEditClick: onEditActionClick,
          onRemoveServiceAccount
        }} />,
        id: 'actions',
        sticky: true,
        stickyPosition: 'right',
        disableSortBy: true,
        disableResizing: true,
        width: 100
      },
    ]
    if (!displayScopeColumn) {
      // Using filter to preserve column order
      return columns.filter(col => col.id !== 'scope')
    }
    return columns
  }, [onRemoveServiceAccount, onEditActionClick, onRemoveActionClick, onRowClick, selectedTreeNode, sgKeyBy])

  const filterRows = useCallback(rows => {
    let array: any[] = rows
    if (rolesEnabled) {
      if (filters.type) {
        switch (filters.type?.id) {
          case IDENTITY_TYPES.USER:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.USER)
            break
          case IDENTITY_TYPES.SERVICE_ACCOUNT:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT)
            break
          case IDENTITY_TYPES.GROUP:
            array = array?.filter(member => member?.identity_type === IDENTITY_TYPES.GROUP)
            break
          case 'none':
            array = array?.filter(member => !ARRAY.hasArray(member?.policies))
            break
          case 'all':
          default:
        }
      }
      if (filters?.app && filters?.app?.id) {
        array = array.filter(({ apps }) => apps?.includes(filters?.app?.id))
      }
      if (filters?.role && filters?.role?.id) {
        array = filters?.role?.id === 'all' ? array : array.filter(({ roles }) => {
          if (OBJECT.hasObject(roles)) {
            for (const [, roleArray] of Object.entries(roles)) {
              const hasRole = (roleArray as any[]).some(({ role }) => role === filters.role.id)
              if (hasRole) {
                return true
              }
            }
          }
          return false
        })
      }
      if (filters?.inheritedBy && filters?.inheritedBy?.id) {
        array = filters?.inheritedBy?.id === 'all' ? array : array.filter(item => item?.inherited_from?.includes(filters?.inheritedBy?.id))
      }
    }
    return array
  }, [filters])

  const rows = useMemo(() => {
    let array = filterRows(resolvedData)
    if (OBJECT.hasObject(sortBy)) {
      const sortFunction = (itemA: any, itemB: any) => {
        switch (sortBy?.id) {
          case 'applications':
            return sortApps(itemA, itemB, sortBy)
          case 'inherited_from':
            return sortInherited(itemA, itemB, sortBy, sgKeyBy)
          case 'role':
            return sortRoles(itemA, itemB, sortBy, roleMap, customRoleMap)
          case 'identity_email':
          case 'display_name':
          case 'identity_type':
          default:
            return defaultSort(itemA, itemB, sortBy)
        }
      }
      array = array.sort(sortFunction)
    }
    return array
  }, [filterRows, resolvedData, sortBy, roleMap, customRoleMap, sgKeyBy])

  const RightControls = () => {
    const selected = useSelectedRowData() || []
    const hasSelected = ARRAY.hasArray(selected)
    const hasInherited = selected.some(user => ARRAY.hasArray(user?.inherited_from) && !user?.inherited_from.every((id: any) => id === selectedTreeNode?.id))
    const isOnlyOne = accessPolicies?.length === 1 && ['msp_superuser', 'superuser'].includes(accessPolicies?.[0]?.role)
    return (
      <>
        {/* Button Remove */}
        {hasInherited || isOnlyOne || readOnly ? (
          <TooltipButton
            label={hasInherited ? TOOLTIP_SELECTED_INHERITED_POLICY :
              isOnlyOne ? TOOLTIP_SELECTED_LAST_SUPERUSER :
                TOOLTIP_READ_ONLY }
            disabled
            tooltipProps={{ defaultPlacement: 'bottom-end' }}
            buttonProps = {{ appearance: hasSelected ? 'secondary-destructive' : 'tertiary' }}>
            {BUTTON_REMOVE_ROLES}
          </TooltipButton>
        ) : (
          <div className='tw-p-0.5'>
            <Button
              appearance={hasSelected ? 'secondary-destructive' : 'tertiary'}
              disabled={!hasSelected || !Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID}
              onClick={() => removeModal.openModal({
                tenant: selectedTreeNode,
                getData,
                removeIdentity,
                identities: Object.values(selected),
              } as any)}
            >{BUTTON_REMOVE_ROLES}</Button>
          </div>
        )}
        {/* Button Add Roles */}
        {readOnly ? (
          <TooltipButton
            label={TOOLTIP_READ_ONLY }
            disabled
            tooltipProps={{ defaultPlacement: 'bottom-end' }}
            buttonProps = {{ appearance: 'secondary' }}>
            {BUTTON_ASSIGN_ROLES}
          </TooltipButton>
        ) : (
          <div className="relative">
            <Button
              appearance={'secondary'}
              disabled={readOnly || !hasSelected || !Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID}
              onClick={() => assignRoles.openModal({ tenant: selectedTreeNode, selected, getData } as any)}
            >{BUTTON_ASSIGN_ROLES}</Button>
          </div>
        )}
        {/* Button Add Identity */}
        {rolesEnabled && readOnly ? (
          <TooltipButton
            label={TOOLTIP_READ_ONLY }
            disabled
            tooltipProps={{ defaultPlacement: 'bottom-end' }}
            buttonProps = {{ appearance: 'primary' }}>
            {BUTTON_ADD_IDENTITY}
          </TooltipButton>
        ) : (
          <Button
            appearance='primary'
            onClick={() => addModal.openModal({ tenant: selectedTreeNode, getData, addIdentity, removeIdentity })}
            disabled={readOnly || !Boolean(selectedTreeNode) || !Boolean(selectedTreeNode?.id) || selectedTreeNode?.id === ROOT_NODE_ID || ARRAY.hasArray(selected)}
          >{BUTTON_ADD_IDENTITY}</Button>
        )}
      </>
    )
  }

  const title = useMemo(() => {
    return (<div className='tw-flex tw-gap-2 tw-overflow-hidden'>{LABEL_IDENTITIES_SUBTENANT}<TruncateText addClassName="tw-block">{selectedTreeNode?.display_name}</TruncateText></div>)
  }, [selectedTreeNode?.display_name])

  return (
    <>
      {rolesEnabled && <FiltersBar {...{ setFilters, apps, inherited, roles }} />}
      <div className="tw-relative tw-w-full tw-h-full">
        <V5TableWrapper id="identities-container" className="tw-flex tw-flex-grow tw-flex-col tw-p-3 tw-absolute tw-inset-0">
          <TableProvider
            {...{ columns } as any}
            data={rows}
            enableColumnSort
            manualSortBy
            enablePagination
            enableRowSelect
            enableResizeColumns
            enableColumnOrder
            isLoading={loading}
            onSortChange={onSortChange}
          >
            <TableHeader title={title as any} subtitle={LABEL_HEADER_SUBTITLE}>
              <ConnectedTableFilter placeholder={PLACEHOLDER_SEARCH}/>
              <RightControls/>
            </TableHeader>
            <ConnectedTableLayout
              sidecar={<div/>}
              overflow
              paginator={<ConnectedPaginator/>}
            >
              <ConnectedTable
                emptyStateProps={{
                  heading: canListPolicies ? 'No Results Available' : 'You don\'t have access to view this page.',
                }}
                appearance='primary'
                shouldHighlightRow={shouldHighlightRow}
              />
            </ConnectedTableLayout>
          </TableProvider>
        </V5TableWrapper>
      </div>
    </>
  )
}

export default IdentitiesTable
